import React from 'react';
import { CheckIcon, BuildingIcon, UsersIcon, ThumbsUpIcon, ClipboardCheckIcon, BarChart3Icon, ShieldIcon, TrendingUpIcon, XIcon, PlusIcon } from 'lucide-react';
export function PersonalizationLoop({
  data,
  updateData
}) {
  const usefulElements = [{
    id: 'company-info',
    title: 'Company Information',
    icon: <BuildingIcon className="h-5 w-5" data-id="element-722" />,
    description: 'Background, size, funding, and industry context'
  }, {
    id: 'attendee-insights',
    title: 'Attendee Insights',
    icon: <UsersIcon className="h-5 w-5" data-id="element-723" />,
    description: 'Professional background, roles, and LinkedIn profiles'
  }, {
    id: 'product-fit',
    title: 'Product Fit Analysis',
    icon: <ThumbsUpIcon className="h-5 w-5" data-id="element-724" />,
    description: 'How your solution aligns with their needs'
  }, {
    id: 'action-suggestions',
    title: 'Action Suggestions',
    icon: <ClipboardCheckIcon className="h-5 w-5" data-id="element-725" />,
    description: 'Recommended next steps and talking points'
  }];
  const missingElements = [{
    id: 'competitors',
    title: 'Competitor Analysis',
    icon: <BarChart3Icon className="h-5 w-5" data-id="element-726" />,
    description: 'Insights on competing solutions they may be evaluating'
  }, {
    id: 'objections',
    title: 'Objection Handling',
    icon: <ShieldIcon className="h-5 w-5" data-id="element-727" />,
    description: 'Common objections and effective responses'
  }, {
    id: 'industry-trends',
    title: 'Industry Trends',
    icon: <TrendingUpIcon className="h-5 w-5" data-id="element-728" />,
    description: 'Relevant market trends and industry developments'
  }];
  const toggleUsefulElement = elementId => {
    const current = [...data.usefulElements];
    if (current.includes(elementId)) {
      updateData('usefulElements', current.filter(id => id !== elementId));
    } else {
      updateData('usefulElements', [...current, elementId]);
    }
  };
  const toggleMissingElement = elementId => {
    const current = [...data.missingElements];
    if (current.includes(elementId)) {
      updateData('missingElements', current.filter(id => id !== elementId));
    } else {
      updateData('missingElements', [...current, elementId]);
    }
  };
  return <div className="w-full" data-id="element-729">
      <h2 className="text-2xl font-bold mb-2" data-id="element-730">Personalize Your Experience</h2>
      <p className="text-[#F5F7FA] text-opacity-70 mb-6" data-id="element-731">
        Help us fine-tune your meeting briefs by telling us what information you
        find most valuable.
      </p>
      <div className="space-y-6 w-full" data-id="element-732">
        <div className="w-full" data-id="element-733">
          <h3 className="text-lg font-medium mb-3" data-id="element-734">
            What did you find most useful in the sample brief?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full" data-id="element-735">
            {usefulElements.map(element => <div key={element.id} onClick={() => toggleUsefulElement(element.id)} className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all ${data.usefulElements.includes(element.id) ? 'border-[#C8F169] bg-[#C8F169] bg-opacity-5' : 'border-[#F5F7FA] border-opacity-10 hover:border-[#F5F7FA] hover:border-opacity-30'}`} data-id="element-736">
                <div className={`p-1.5 rounded-lg mr-3 ${data.usefulElements.includes(element.id) ? 'bg-[#C8F169] bg-opacity-20 text-[#C8F169]' : 'bg-[#F5F7FA] bg-opacity-10 text-[#F5F7FA]'}`} data-id="element-737">
                  {element.icon}
                </div>
                <div className="flex-1" data-id="element-738">
                  <h4 className="font-medium text-sm" data-id="element-739">{element.title}</h4>
                  <p className="text-xs text-[#F5F7FA] text-opacity-70" data-id="element-740">
                    {element.description}
                  </p>
                </div>
                <div className={`ml-2 h-5 w-5 rounded-full flex-shrink-0 flex items-center justify-center ${data.usefulElements.includes(element.id) ? 'bg-[#C8F169] text-[#1E2A38]' : 'border border-[#F5F7FA] border-opacity-30'}`} data-id="element-741">
                  {data.usefulElements.includes(element.id) && <CheckIcon className="h-3 w-3" data-id="element-742" />}
                </div>
              </div>)}
          </div>
        </div>
        <div className="w-full" data-id="element-743">
          <h3 className="text-lg font-medium mb-3" data-id="element-744">
            What was missing that you'd like to see?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full" data-id="element-745">
            {missingElements.map(element => <div key={element.id} onClick={() => toggleMissingElement(element.id)} className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all ${data.missingElements.includes(element.id) ? 'border-[#C8F169] bg-[#C8F169] bg-opacity-5' : 'border-[#F5F7FA] border-opacity-10 hover:border-[#F5F7FA] hover:border-opacity-30'}`} data-id="element-746">
                <div className={`p-1.5 rounded-lg mr-3 ${data.missingElements.includes(element.id) ? 'bg-[#C8F169] bg-opacity-20 text-[#C8F169]' : 'bg-[#F5F7FA] bg-opacity-10 text-[#F5F7FA]'}`} data-id="element-747">
                  {element.icon}
                </div>
                <div className="flex-1" data-id="element-748">
                  <h4 className="font-medium text-sm" data-id="element-749">{element.title}</h4>
                  <p className="text-xs text-[#F5F7FA] text-opacity-70" data-id="element-750">
                    {element.description}
                  </p>
                </div>
                <div className={`ml-2 h-5 w-5 rounded-full flex-shrink-0 flex items-center justify-center ${data.missingElements.includes(element.id) ? 'bg-[#C8F169] text-[#1E2A38]' : 'border border-[#F5F7FA] border-opacity-30'}`} data-id="element-751">
                  {data.missingElements.includes(element.id) && <PlusIcon className="h-3 w-3" data-id="element-752" />}
                </div>
              </div>)}
          </div>
        </div>
        <div className="bg-[#1E2A38] border border-[#F5F7FA] border-opacity-10 rounded-lg p-4 w-full" data-id="element-753">
          <h4 className="font-medium mb-2" data-id="element-754">Any other feedback?</h4>
          <textarea className="w-full bg-[#141E2A] border border-[#F5F7FA] border-opacity-10 rounded-lg px-4 py-2 text-[#F5F7FA] focus:outline-none focus:ring-2 focus:ring-[#C8F169] focus:ring-opacity-50 h-24 resize-none" placeholder="Share any additional insights or features you'd like to see..." data-id="element-755"></textarea>
        </div>
        <div className="bg-[#C8F169] bg-opacity-5 border border-[#C8F169] border-opacity-20 rounded-lg p-4 w-full" data-id="element-756">
          <h4 className="font-medium text-[#C8F169] mb-2" data-id="element-757">You're all set!</h4>
          <p className="text-sm text-[#F5F7FA]" data-id="element-758">
            Thanks for your feedback! We'll use this information to personalize
            your meeting briefs. Click "Complete" to start using Prepz.io.
          </p>
        </div>
      </div>
    </div>;
}